// src/utils/kpi-tooltips.tsx
import React from 'react';
import { TrendingUp, DollarSign, Percent, RefreshCw, Layers, Coins, Target, TrendingDown, AlertCircle } from 'lucide-react';

export const KPI_TOOLTIPS = {
  portfolioPct: {
    title: 'Profit % (portfolio)',
    content: (
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Profit % (portfolio)</h4>
        <p className="text-gray-300 text-sm">
          Cost-weighted return on investment from closed positions.
        </p>
        <div className="pt-2 border-t border-gray-700">
          <h5 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
            <Percent className="h-4 w-4" />
            Formula
          </h5>
          <p className="font-mono text-xs text-gray-300 bg-gray-800 p-2 rounded">
            total_profit_usd ÷ total_cost_usd × 100
          </p>
        </div>
        <div className="space-y-1 text-xs text-gray-400">
          <p>• Calculated only from closed trades using FIFO cost basis</p>
          <p>• Individual pair % values don't sum to portfolio % (weighted)</p>
        </div>
      </div>
    )
  },
  portfolioUsd: {
    title: 'Profit $ (portfolio)',
    content: (
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Profit $ (portfolio)</h4>
        <p className="text-gray-300 text-sm">
          Net profit from all closed positions after deducting trading fees.
        </p>
        <div className="pt-2 border-t border-gray-700">
          <h5 className="font-medium text-green-400 mb-2 flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Formula
          </h5>
          <p className="font-mono text-xs text-gray-300 bg-gray-800 p-2 rounded">
            gross_profit - total_fees
          </p>
        </div>
        <div className="space-y-1 text-xs text-gray-400 pt-2">
          <p>• Net profit is used (after deducting fees)</p>
          <p>• This is your actual profit after all trading costs</p>
          <p>• Fees are shown separately for transparency</p>
        </div>
      </div>
    )
  },
  roeInitial: {
    title: 'ROE % (initial balance)',
    content: (
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">ROE % (initial balance)</h4>
        <p className="text-gray-300 text-sm">
          Return on equity based on your account balance from bot settings (Amounts section).
        </p>
        <div className="pt-2 border-t border-gray-700">
          <h5 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
            <Percent className="h-4 w-4" />
            Formula
          </h5>
          <p className="font-mono text-xs text-gray-300 bg-gray-800 p-2 rounded">
            net_profit ÷ account_balance × 100
          </p>
        </div>
        <div className="space-y-1 text-xs text-gray-400">
          <p>• Net profit is used (after deducting fees)</p>
          <p>• Account balance is taken from your bot configuration</p>
          <p>• More intuitive percentage for investors</p>
        </div>
      </div>
    )
  },
  turnover: {
    title: 'Turnover (×)',
    content: (
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Turnover (×)</h4>
        <p className="text-gray-300 text-sm">
          How many times your initial balance "rotated" during the period.
        </p>
        <div className="pt-2 border-t border-gray-700">
          <h5 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Formula
          </h5>
          <p className="font-mono text-xs text-gray-300 bg-gray-800 p-2 rounded">
            total_volume_usd ÷ initial_balance
          </p>
        </div>
        <div className="space-y-1 text-xs text-gray-400">
          <p>• If Turnover = 66.59×, total trading volume was ~67× your starting amount</p>
          <p>• High turnover indicates active trading</p>
        </div>
      </div>
    )
  },
  totalCost: {
    title: 'Total Cost (USD)',
    content: (
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Total Cost (USD)</h4>
        <p className="text-gray-300 text-sm">
          Total trading volume: sum of all buy and sell transaction values.
        </p>
        <div className="pt-2 border-t border-gray-700">
          <h5 className="font-medium text-yellow-400 mb-2 flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Important
          </h5>
          <div className="space-y-1 text-xs text-gray-300">
            <p>• This is not your account balance</p>
            <p>• Represents total capital turnover (buy volume + sell volume)</p>
            <p>• High volume indicates active trading strategy</p>
          </div>
        </div>
      </div>
    )
  },
  fees: {
    title: 'Fees',
    content: (
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Fees</h4>
        <p className="text-gray-300 text-sm">
          Total commission costs (USD) across all trades.
        </p>
        <div className="space-y-1 text-xs text-gray-400 pt-2">
          <p>• Collected from trade records or calculated by fee rate</p>
          <p>• Already deducted in P&L calculations</p>
        </div>
      </div>
    )
  },
  trades: {
    title: 'Trades',
    content: (
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Trades</h4>
        <p className="text-gray-300 text-sm">
          Total number of recorded trade events (BUY + SELL).
        </p>
        <div className="space-y-1 text-xs text-gray-400 pt-2">
          <p>• This is not the number of positions</p>
          <p>• Each buy/sell is counted separately</p>
        </div>
      </div>
    )
  },
  winRate: {
    title: 'Win Rate',
    content: (
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Win Rate</h4>
        <p className="text-gray-300 text-sm">
          Percentage of profitable exits (SELL orders with P&L {'>'} 0).
        </p>
        <div className="pt-2 border-t border-gray-700">
          <h5 className="font-medium text-green-400 mb-2 flex items-center gap-2">
            <Target className="h-4 w-4" />
            Formula
          </h5>
          <p className="font-mono text-xs text-gray-300 bg-gray-800 p-2 rounded">
            winning_trades ÷ total_closed_sells × 100
          </p>
        </div>
        <div className="text-xs text-gray-400 pt-2">
          <p>• High win rate {'>'} 50% indicates good strategy accuracy</p>
        </div>
      </div>
    )
  },
  pairPct: {
    title: 'Pair Profit %',
    content: (
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Pair Profit %</h4>
        <p className="text-gray-300 text-sm">
          Individual pair return based on that pair's cost basis.
        </p>
        <div className="pt-2 border-t border-gray-700">
          <h5 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
            <Coins className="h-4 w-4" />
            Formula
          </h5>
          <p className="font-mono text-xs text-gray-300 bg-gray-800 p-2 rounded">
            pair_profit_usd ÷ pair_cost_usd × 100
          </p>
        </div>
        <div className="space-y-1 text-xs text-gray-400 pt-2">
          <p>• Average of pair % ≠ portfolio %</p>
          <p>• Portfolio is calculated using cost-weighted methodology</p>
        </div>
      </div>
    )
  },
  maxDrawdown: {
    title: 'Max Drawdown',
    content: (
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Max Drawdown</h4>
        <p className="text-gray-300 text-sm">
          Largest portfolio value decline from a peak to a trough.
        </p>
        <div className="pt-2 border-t border-gray-700">
          <h5 className="font-medium text-red-400 mb-2 flex items-center gap-2">
            <TrendingDown className="h-4 w-4" />
            Risk Indicator
          </h5>
          <div className="space-y-1 text-xs text-gray-300">
            <p>• Shows maximum risk exposure during the period</p>
            <p>• Lower drawdown = more stable strategy</p>
          </div>
        </div>
      </div>
    )
  }
} as const;

export type KpiKey = keyof typeof KPI_TOOLTIPS;
